import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useGetNurseDashboard } from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { FlaskConical, ArrowLeftRight, Zap, BookOpen, MessageSquare, AlertCircle, ArrowRight, Scale, ClipboardList, CheckCircle2, Plus } from "lucide-react";
import { Link } from "wouter";
import { useState, useEffect } from "react";

interface CpdSummary {
  count: number;
  reflect: number;
  plan: number;
  act: number;
  evaluate: number;
  year: number;
}

const CPD_TARGET = 9;

export default function PharmacistDashboard() {
  const { data: dashboard, isLoading, error } = useGetNurseDashboard();
  const [cpdSummary, setCpdSummary] = useState<CpdSummary | null>(null);

  useEffect(() => {
    fetch("/api/pharmacist/cpd/summary")
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setCpdSummary(data); })
      .catch(() => {});
  }, []);

  if (isLoading) return <PharmacistLayout title="Dashboard"><LoadingScreen /></PharmacistLayout>;
  if (error || !dashboard) return <PharmacistLayout title="Dashboard"><div>Error loading dashboard</div></PharmacistLayout>;

  const quickCards = [
    {
      href: "/pharmacist/monitoring",
      icon: FlaskConical,
      title: "DMARD & Biologic Monitoring",
      desc: "Monitoring schedules, blood parameters, action thresholds for all DMARDs and biologics",
      colour: "text-violet-600",
      bg: "bg-violet-50",
    },
    {
      href: "/pharmacist/interactions",
      icon: Zap,
      title: "Drug Interactions",
      desc: "Check interactions between rheumatology drugs with pharmacist-level clinical management guidance",
      colour: "text-amber-600",
      bg: "bg-amber-50",
    },
    {
      href: "/pharmacist/biosimilars",
      icon: ArrowLeftRight,
      title: "Biosimilar Guide",
      desc: "UK-approved biosimilars, NHSE switching policy, counselling points and nocebo management",
      colour: "text-teal-600",
      bg: "bg-teal-50",
    },
    {
      href: "/pharmacist/dose-optimisation",
      icon: Scale,
      title: "Dose Optimisation",
      desc: "Renal and hepatic impairment dose adjustment tables with eGFR thresholds",
      colour: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      href: "/pharmacist/counselling",
      icon: ClipboardList,
      title: "Patient Counselling",
      desc: "Structured counselling frameworks for DMARD and biologic initiation and counselling",
      colour: "text-emerald-600",
      bg: "bg-emerald-50",
    },
    {
      href: "/pharmacist/chat",
      icon: MessageSquare,
      title: "AI Medicines Adviser",
      desc: "Medicines-optimisation queries grounded in GPhC Standards, RPS, BNF, and NHSE frameworks",
      colour: "text-violet-600",
      bg: "bg-violet-50",
    },
  ];

  return (
    <PharmacistLayout title="Dashboard">
      <div className="grid gap-6 md:grid-cols-3 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Monitoring Alerts</CardTitle>
            <AlertCircle className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">Review Due</div>
            <p className="text-xs text-slate-500 mt-1">
              <Link href="/pharmacist/monitoring" className="text-violet-600 hover:underline">
                Check DMARD monitoring schedules
              </Link>
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">GPhC CPD Portfolio {cpdSummary ? `(${cpdSummary.year})` : ""}</CardTitle>
            {cpdSummary && cpdSummary.count >= CPD_TARGET
              ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              : <BookOpen className="h-4 w-4 text-slate-500" />
            }
          </CardHeader>
          <CardContent>
            {cpdSummary ? (
              <>
                <div className={`text-2xl font-bold ${cpdSummary.count >= CPD_TARGET ? "text-emerald-600" : "text-violet-700"}`}>
                  {cpdSummary.count} / {CPD_TARGET}
                </div>
                <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-1.5 rounded-full transition-all ${cpdSummary.count >= CPD_TARGET ? "bg-emerald-500" : "bg-violet-500"}`}
                    style={{ width: `${Math.min((cpdSummary.count / CPD_TARGET) * 100, 100)}%` }}
                  />
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <p className="text-xs text-slate-500">
                    {cpdSummary.count >= CPD_TARGET
                      ? "GPhC minimum met ✓"
                      : `${CPD_TARGET - cpdSummary.count} entries to GPhC minimum`}
                  </p>
                  <Link href="/pharmacist/cpd" className="text-xs text-violet-600 hover:underline flex items-center gap-1">
                    <Plus className="h-3 w-3" />Add entry
                  </Link>
                </div>
              </>
            ) : (
              <>
                <div className="text-sm font-medium text-violet-600">CPD Activity Log</div>
                <p className="text-xs text-slate-500 mt-1">
                  <Link href="/pharmacist/cpd" className="text-violet-600 hover:underline">Track your GPhC CPD entries</Link>
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Guidelines in Reference</CardTitle>
            <BookOpen className="h-4 w-4 text-slate-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard.totalGuidelines}</div>
            <p className="text-xs text-slate-500">Active NICE/BSR/EULAR/ACR guidelines</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-8">
        {quickCards.map((card) => (
          <Link key={card.href} href={card.href}>
            <div className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md hover:border-violet-200 transition-all cursor-pointer group h-full">
              <div className={`${card.bg} ${card.colour} h-10 w-10 rounded-xl flex items-center justify-center mb-3 group-hover:scale-105 transition-transform`}>
                <card.icon className="h-5 w-5" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-violet-700 transition-colors">{card.title}</h3>
              <p className="text-xs text-slate-500 leading-relaxed">{card.desc}</p>
            </div>
          </Link>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Guideline Updates</CardTitle>
          <CardDescription>Latest updates relevant to medicines optimisation in rheumatology</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          {dashboard.recentGuidelines.map((g) => (
            <div key={g.id} className="flex items-center justify-between group">
              <div className="space-y-1">
                <p className="text-sm font-medium leading-none">{g.title}</p>
                <p className="text-sm text-slate-500">{g.organisation} · {g.category}</p>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </div>
          ))}
        </CardContent>
      </Card>
    </PharmacistLayout>
  );
}
